var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import ProductWithData from '../Product/Product';
import Button from '../Button/Button';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { trackOptimizelyEvent } from '../../util';
import { replaceStateNextCompat } from '../../util/history';
import * as Styled from './QuickView.styled';
import { useQuickView } from './QuickView.context';
/**
 * A popup view for streamlined product viewing
 *
 * Use QuickViewWithData component to fetch data
 * @see QuickViewWithData
 */
var QuickViewBody = function (_a) {
    var id = _a.id, onRequestClose = _a.onRequestClose;
    if (!id)
        return null;
    var _b = useQuickView(), containerStylesApplied = _b.containerStylesApplied, setContainerStylesApplied = _b.setContainerStylesApplied, apolloError = _b.apolloError, PDPLink = _b.PDPLink;
    useEffect(function () {
        trackOptimizelyEvent('Opens_QuickView');
    }, []);
    useEffect(function () {
        var _a;
        setContainerStylesApplied(true);
        /**
         * PDPLink's value changes when the product is loaded in for the first time and when a variant is selected.
         *
         * This ensures that the URL is updated to reflect the current product/variant being viewed in QuickView
         * without adding new entries to the browser history.
         */
        if (PDPLink &&
            window.location.pathname + window.location.search !== PDPLink) {
            replaceStateNextCompat(null, '', PDPLink);
        }
        /**
         * This call also happens in ProductReviews.js
         *
         * Due to inconsistent behavior, this call needs to happen here to ensure that reviews always show
         */
        (_a = window.yotpo) === null || _a === void 0 ? void 0 : _a.refreshWidgets();
    }, [PDPLink]);
    if (apolloError) {
        var errorTitle = apolloError.networkError
            ? 'Something Went Wrong'
            : 'Product Not Found';
        var errorMessage = apolloError.networkError
            ? "Something isn't working, please try again later."
            : 'We could not find the product you were looking for.';
        return (React.createElement(Styled.Error, null,
            React.createElement("div", null,
                React.createElement("h3", null, errorTitle),
                React.createElement("p", null, errorMessage),
                React.createElement(Button, { onClick: onRequestClose, variant: "primary" }, "Keep Shopping"))));
    }
    return (React.createElement(Styled.FadeIn, { isFadingIn: containerStylesApplied },
        React.createElement(ProductWithData, null)));
};
/**
 *
 * QuickView component wrapped with data fetching
 *
 * @see QuickView
 */
var QuickView = function (_a) {
    var rest = __rest(_a, []);
    var router = useRouter();
    var hasUpdatedUrl = useRef(false);
    var _b = useQuickView(), activeProductId = _b.activeProductId, containerStylesApplied = _b.containerStylesApplied, setActiveProductId = _b.setActiveProductId, setApolloError = _b.setApolloError, previousUrl = _b.previousUrl;
    var onRequestClose = function (e) {
        setActiveProductId(null);
        setApolloError(null);
        hasUpdatedUrl.current = false;
        // Only call router.back() if this wasn't triggered by a PopStateEvent (browser back button)
        // Timeout has to be greater than the longest animation duration in OverlayModal (300ms) otherwise the modal won't fully close before the URL changes
        if (!e || e.type !== 'popstate') {
            setTimeout(function () {
                router.back();
            }, 400);
        }
    };
    // Runs this only once when the QuickView is closed and there is a previousUrl to go back to
    useEffect(function () {
        if (!activeProductId && !hasUpdatedUrl.current && previousUrl) {
            hasUpdatedUrl.current = true;
            /**
             * NOTE: Temporary solution until we migrate to App Router and leverage Parallel Routes
             * @see https://nextjs.org/docs/app/api-reference/file-conventions/parallel-routes
             *
             * TODO: Migrate to App Router and use Parallel Routes, then investigate if this is still needed
             *
             * Timeout has to be the same as the timeout in onRequestClose otherwise page might scroll to top.
             * Since the router still thinks it's still on the PCP, we can use router navigation events with shallow routing
             * to update the URL without making a new page request
             */
            setTimeout(function () {
                router.replace(previousUrl, '', { scroll: false, shallow: true });
            }, 400);
        }
    }, [activeProductId, previousUrl]);
    return (React.createElement(ErrorBoundary, null,
        React.createElement(Styled.QuickView, __assign({ isOpen: !!activeProductId, onRequestClose: onRequestClose, modalType: "sheet", size: "xMega", smallLayoutWithSidePadding: true, smallLayoutTopMargin: 0, mediumLayoutTopBottomPadding: { top: 0, bottom: 0 }, withPadding: false }, rest),
            React.createElement(Styled.FullContainer, null,
                !containerStylesApplied && React.createElement(Styled.Loading, null),
                React.createElement(QuickViewBody, { id: activeProductId })))));
};
export { QuickViewBody };
export default QuickView;
